import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import {
  useComposioStatus,
  useComposioConnect,
} from '@/shared/hooks/useComposioConnect';
import { JiraLogo, SharePointLogo, ConfluenceLogo } from './integration-logos';

const AI_TOOLKITS = [
  { slug: 'jira', Logo: JiraLogo },
  { slug: 'share_point', Logo: SharePointLogo },
  { slug: 'confluence', Logo: ConfluenceLogo },
] as const;

type AiToolkit = (typeof AI_TOOLKITS)[number];

export const AiConnectionsTab = () => {
  const { t } = useTranslation('goals');
  const { data: status, isLoading: isStatusLoading } = useComposioStatus();
  const connectMutation = useComposioConnect();
  const connections = status?.connections ?? {};

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mb-5">
        <h3 className="text-base font-semibold">
          {t('integrationsDialog.aiConnections')}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('integrationsDialog.aiConnectionsDescription')}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {AI_TOOLKITS.map((toolkit) => (
          <IntegrationCard
            key={toolkit.slug}
            toolkit={toolkit}
            isConnected={connections[toolkit.slug] ?? false}
            isStatusLoading={isStatusLoading}
            isConnecting={
              connectMutation.isPending &&
              connectMutation.variables === toolkit.slug
            }
            onConnect={() => connectMutation.mutate(toolkit.slug)}
          />
        ))}
      </div>
    </div>
  );
};

interface IntegrationCardProps {
  toolkit: AiToolkit;
  isConnected: boolean;
  isStatusLoading: boolean;
  isConnecting: boolean;
  onConnect: () => void;
}

const IntegrationCard = ({
  toolkit,
  isConnected,
  isStatusLoading,
  isConnecting,
  onConnect,
}: IntegrationCardProps) => {
  const { t } = useTranslation('goals');
  const { Logo, slug } = toolkit;

  return (
    <div className="flex h-36 flex-col rounded-xl border bg-card p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <Logo />
        {isStatusLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : isConnected ? (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
            <CheckCircle2 className="h-4 w-4" />
            {t('integrationsDialog.aiConnected')}
          </span>
        ) : null}
      </div>
      <div className="mt-auto">
        <h4 className="text-sm font-semibold">
          {t(`integrationsDialog.aiToolkits.${slug}.name`)}
        </h4>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t(`integrationsDialog.aiToolkits.${slug}.description`)}
        </p>
      </div>
      {!isConnected && !isStatusLoading && (
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={onConnect}
          disabled={isConnecting}
        >
          {isConnecting ? (
            <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />
          ) : null}
          {t('integrationsDialog.aiConnect')}
        </Button>
      )}
    </div>
  );
};
